import CheckCircleBlack from '@public/icons/check-circle-black.svg';
import CheckCircle from '@public/icons/check-circle-green.svg';
import XCircleRed from '@public/icons/x-circle-red.svg';
import AffirmLogo from '@public/logos/affirm_logo.svg';
import AlterAnywhereImg from '@public/pricing/alter-anywhere.png';
import AlterOneImg from '@public/pricing/alter-one.png';
import clsx from 'clsx';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import Link from 'next/link';

interface Feature {
  text: string;
  crossed?: boolean;
}

const ALTER_ANYWHERE_FEATURES = [
  { text: 'Smart Fitness Screen', crossed: true },
  { text: '3D Body Composition Scanning', crossed: true },
  { text: 'Signal Wearable Device' },
  { text: 'On-Demand Expert Coaching' },
  { text: 'Personalized Workout Plan' },
  { text: 'Daily Mindfulness and Recovery Sessions' },
  { text: 'DNA-based Nutrition Plan' },
  { text: '12 Months of FREE Membership Included' },
];

const ALTER_ONE_FEATURES = [
  { text: 'Smart Fitness Screen' },
  { text: '3D Body Composition Scanning' },
  { text: 'Signal Biometric Wearable Device' },
  { text: 'On-Demand Expert Coaching' },
  { text: 'Personalized Workout Plan' },
  { text: 'Daily Mindfulness and Recovery Sessions' },
  { text: 'DNA-based Nutrition Plan' },
  { text: '24 Months of FREE Membership Included' },
];

const HSA_FSAEligible = () => {
  return (
    <div className="inline-flex items-center gap-1 pl-3 pr-7 py-1 rounded-full border border-alter-black-20 w-min whitespace-nowrap">
      <p className="flex items-center gap-1 uppercase text-xs lg:text-sm py-1">
        <span className="text-alter-black">HSA/FSA</span>
        <span className="text-alter-black-60">Eligible</span>
        <Image src={CheckCircle} width={15} height={15} alt="Checked circle" />
      </p>
    </div>
  );
};

const SystemFeaturesColumn = ({
  title,
  price,
  features,
  image,
}: {
  title: string;
  price: string;
  features: Feature[];
  image: StaticImageData;
}) => {
  return (
    <div className="flex flex-col gap-6 text-alter-black">
      <Image
        src={image}
        alt={title}
        className="object-cover w-full h-auto rounded-2xl border border-alter-black-20"
        priority
        draggable={false}
      />
      <p className="font-light text-xl lg:text-[28px]">{title}</p>
      <div className="flex flex-col gap-2">
        <p className="uppercase text-xs lg:text-sm text-alter-black-60">Starting at</p>
        <p className="font-light text-2xl lg:text-[28px]">{price}</p>
        <p className="font-light text-sm lg:text-lg text-alter-black-60 flex flex-wrap items-baseline gap-1.5">
          <span>36 monthly payments with</span>
          <Image src={AffirmLogo} width={40} height={20} alt="Affirm Logo" className="shrink-0" />
        </p>
        <Link
          className="font-light text-sm"
          href="https://www.affirm.com/disclosures"
          target="blank"
        >
          *Affirm disclosures
        </Link>
      </div>
      <HSA_FSAEligible />
      <div className="flex flex-col gap-3 lg:gap-4 text-alter-black">
        <p className="uppercase text-xs lg:text-sm text-alter-black-60">Includes:</p>
        {features.map((feature, index) => (
          <div key={feature.text} className="flex items-center gap-2 lg:text-lg">
            {feature.crossed ? (
              <Image
                src={XCircleRed}
                width={24}
                height={24}
                alt="Checked circle"
                className="opacity-50"
              />
            ) : (
              <Image src={CheckCircleBlack} width={24} height={24} alt="Checked circle" />
            )}
            <p
              className={clsx('font-light', feature.crossed && 'line-through text-alter-black-60')}
            >
              {feature.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SystemFeaturesList = () => {
  return (
    <div className="grid grid-cols-2 gap-4 lg:gap-10">
      <SystemFeaturesColumn
        title="Alter Anywhere"
        price="$83/month"
        features={ALTER_ANYWHERE_FEATURES}
        image={AlterAnywhereImg}
      />
      <SystemFeaturesColumn
        title="Alter One"
        price="$99/month"
        features={ALTER_ONE_FEATURES}
        image={AlterOneImg}
      />
    </div>
  );
};

export default SystemFeaturesList;
